import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import ClientProviderSelect from "@/components/ui/client-provider-select";
import CurrencyInput from "@/components/ui/currency-input";
import DateField from "@/components/ui/date-field";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import IntegerInput from "@/components/ui/integer-input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Skeleton } from "@/components/ui/skeleton";
import { SubmitButton } from "@/components/ui/submit-button";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import VehicleSelect from "@/components/ui/vehicle-select";
import { useFormSubmit } from "@/hooks/useFormSubmit";
import { errorHandler } from "@/lib/utils";
import { maintenanceService } from "@/services/api/maintenances";
import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import { useForm, type Resolver } from "react-hook-form";
import { useNavigate, useParams } from "react-router";
import { z } from "zod";

const createSchema = z
  .object({
    vehicle_id: z.number().min(1, "Selecione um veículo"),
    description: z.string().min(1, "Descrição é obrigatória"),
    provider_id: z.number().nullable(),
    type: z.enum(["odometer", "date", "both"]),
    odometer: z.number().nullable(),
    date: z.string().nullable(),
    value: z.number().nullable(),
    notification_odometer: z.boolean().optional().default(false),
    notification_date: z.boolean().optional().default(false),
    notification_odometer_1000: z.boolean().optional().default(false),
    notification_odometer_2500: z.boolean().optional().default(false),
    notification_odometer_5000: z.boolean().optional().default(false),
    notification_date_15: z.boolean().optional().default(false),
    notification_date_30: z.boolean().optional().default(false),
    is_recurring: z.boolean().optional().default(false),
    interval_km: z.number().positive().nullable().default(null),
    interval_months: z.number().int().positive().nullable().default(null),
  })
  .superRefine((values, ctx) => {
    if (!values.is_recurring) return;

    if (
      (values.type === "odometer" || values.type === "both") &&
      !values.interval_km
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Informe o intervalo em km para a recorrência.",
        path: ["interval_km"],
      });
    }

    if (
      (values.type === "date" || values.type === "both") &&
      !values.interval_months
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Informe o intervalo em meses para a recorrência.",
        path: ["interval_months"],
      });
    }
  });

const editSchema = z.object({
  vehicle_id: z.number().min(1, "Selecione um veículo"),
  description: z.string().min(1, "Descrição é obrigatória"),
  provider_id: z.number().nullable(),
  odometer: z.coerce.number().optional(),
  occurrence_date: z.string().date("Data inválida").nullable(),
  value: z.number().nullable(),
  invoice: z.string().nullable(),
  is_recurring: z.boolean().optional(),
  interval_km: z.number().positive().nullable().optional(),
  interval_months: z.number().int().positive().nullable().optional(),
});

type FormValuesCreateMaintenance = z.infer<typeof createSchema>;
type FormValuesEditMaintenance = z.infer<typeof editSchema>;

const MaintenanceForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [showOdometerFields, setShowOdometerFields] = useState(false);
  const [showDateFields, setShowDateFields] = useState(false);
  const [recurringTemplateId, setRecurringTemplateId] = useState<number | null>(
    null,
  );
  const [occurrenceType, setOccurrenceType] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(!!id);

  const form = useForm<FormValuesCreateMaintenance | FormValuesEditMaintenance>(
    {
      // ponytail: resolvers 5 types the raw input apart from the parsed
      // output; the form keeps the parsed type, as it did on resolvers 3.
      resolver: zodResolver(id ? editSchema : createSchema) as Resolver<
        FormValuesCreateMaintenance | FormValuesEditMaintenance
      >,
      defaultValues: id
        ? {
            vehicle_id: 0,
            description: "",
            provider_id: null,
            odometer: null,
            occurrence_date: format(new Date(), "yyyy-MM-dd"),
            value: null,
            invoice: "",
          }
        : {
            vehicle_id: 0,
            description: "",
            provider_id: null,
            type: "odometer",
            odometer: null,
            date: "",
            value: null,
            notification_odometer: false,
            notification_date: false,
            notification_odometer_1000: false,
            notification_odometer_2500: false,
            notification_odometer_5000: false,
            notification_date_15: false,
            notification_date_30: false,
            is_recurring: false,
            interval_km: null,
            interval_months: null,
          },
    },
  );

  const maintenanceType = form.watch("type");
  const notificationOdometer = form.watch("notification_odometer");
  const notificationDate = form.watch("notification_date");
  const isRecurring = form.watch("is_recurring");

  useEffect(() => {
    if (!id) {
      setShowOdometerFields(
        maintenanceType === "odometer" || maintenanceType === "both",
      );
      setShowDateFields(
        maintenanceType === "date" || maintenanceType === "both",
      );
    }
  }, [maintenanceType, id]);

  useEffect(() => {
    const loadMaintenance = async () => {
      if (id) {
        try {
          setIsLoading(true);
          const response = await maintenanceService.show(Number(id));
          const data = response.data;

          form.reset({
            vehicle_id: data.vehicle_id,
            description: data.description,
            provider_id: data.client_provider_id,
            odometer: data.odometer ? Math.floor(data.odometer) : null,
            occurrence_date: data.occurrence_date,
            value: data.amount ? Number(data.amount) : null,
            invoice: data.invoice_number,
            is_recurring:
              data.recurring_vehicle_maintenance?.is_active ?? false,
            interval_km:
              data.recurring_vehicle_maintenance?.interval_km ?? null,
            interval_months:
              data.recurring_vehicle_maintenance?.interval_months ?? null,
          });
          setRecurringTemplateId(data.recurring_vehicle_maintenance_id);
          setOccurrenceType(data.type);
        } catch (err) {
          errorHandler(err, "Erro ao carregar manutenção");
          navigate("/maintenances");
        } finally {
          setIsLoading(false);
        }
      }
    };

    loadMaintenance();
  }, [id, form.reset, navigate, form]);

  const { handleSubmit, loading } = useFormSubmit({
    id,
    createService: maintenanceService.store,
    updateService: maintenanceService.update,
    onSuccess: () => navigate("/maintenances"),
  });

  const onSubmit = async (
    values: FormValuesCreateMaintenance | FormValuesEditMaintenance,
  ) => {
    const createValues = !id ? (values as FormValuesCreateMaintenance) : null;
    const defaultNotifications = createValues
      ? {
          notification_date_15: createValues.notification_date
            ? createValues.notification_date_15 || 0
            : 0,
          notification_date_30: createValues.notification_date
            ? createValues.notification_date_30 || 0
            : 0,
          notification_odometer_1000: createValues.notification_odometer
            ? createValues.notification_odometer_1000 || 0
            : 0,
          notification_odometer_2500: createValues.notification_odometer
            ? createValues.notification_odometer_2500 || 0
            : 0,
          notification_odometer_5000: createValues.notification_odometer
            ? createValues.notification_odometer_5000 || 0
            : 0,
        }
      : {};

    const submitData = id
      ? {
          ...values,
          client_provider_id:
            (values.provider_id ? values.provider_id : null) ?? null,
          provider_id: (values.provider_id ? values.provider_id : null) ?? null,
        }
      : {
          ...values,
          occurrence_date: id
            ? (values as FormValuesCreateMaintenance).date
            : undefined,
          ...defaultNotifications,
        };

    await handleSubmit(submitData);
  };

  const renderOdometerField = () => (
    <FormField
      control={form.control}
      name="odometer"
      render={({ field }) => (
        <FormItem>
          <FormLabel>Odômetro</FormLabel>
          <FormControl>
            <IntegerInput value={field.value} onChange={field.onChange} />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );

  const renderBasicDataFields = () => (
    <>
      <FormField
        control={form.control}
        name="vehicle_id"
        render={({ field }) => (
          <FormItem className="flex flex-col">
            <FormLabel>Veículo</FormLabel>
            <VehicleSelect value={field.value} onChange={field.onChange} />
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="description"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Descrição</FormLabel>
            <FormControl>
              <Textarea className="dark:border-white" {...field} rows={5} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="provider_id"
        render={({ field }) => (
          <FormItem className="flex flex-col">
            <FormLabel>Fornecedor</FormLabel>
            <ClientProviderSelect
              value={field.value ?? undefined}
              onChange={field.onChange}
            />
            <FormMessage />
          </FormItem>
        )}
      />
    </>
  );

  const renderValueField = () => (
    <FormField
      control={form.control}
      name="value"
      render={({ field }) => (
        <FormItem>
          <FormLabel>Valor (R$)</FormLabel>
          <FormControl>
            <CurrencyInput value={field.value} onChange={field.onChange} />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );

  const renderRecurringIntervalFields = (type: string | null) => (
    <div className="space-y-4 p-4">
      {(type === "odometer" || type === "both") && (
        <FormField
          control={form.control}
          name="interval_km"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Repetir a cada (km)</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  value={field.value ?? ""}
                  onChange={(e) =>
                    field.onChange(
                      e.target.value ? Number(e.target.value) : null,
                    )
                  }
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      {(type === "date" || type === "both") && (
        <FormField
          control={form.control}
          name="interval_months"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Repetir a cada (meses)</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  value={field.value ?? ""}
                  onChange={(e) =>
                    field.onChange(
                      e.target.value ? Number(e.target.value) : null,
                    )
                  }
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
    </div>
  );

  const renderCreateForm = () => (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Dados básicos</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {renderBasicDataFields()}
          {renderValueField()}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Agendamento</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <FormField
            control={form.control}
            name="type"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Tipo de agendamento</FormLabel>
                <FormControl>
                  <RadioGroup
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                    className="flex flex-row space-x-4"
                  >
                    <FormItem className="flex items-center space-x-2">
                      <FormControl>
                        <RadioGroupItem value="odometer" />
                      </FormControl>
                      <FormLabel className="font-normal">Odômetro</FormLabel>
                    </FormItem>
                    <FormItem className="flex items-center space-x-2">
                      <FormControl>
                        <RadioGroupItem value="date" />
                      </FormControl>
                      <FormLabel className="font-normal">Data</FormLabel>
                    </FormItem>
                    <FormItem className="flex items-center space-x-2">
                      <FormControl>
                        <RadioGroupItem value="both" />
                      </FormControl>
                      <FormLabel className="font-normal">
                        O que atingir primeiro
                      </FormLabel>
                    </FormItem>
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {showOdometerFields && renderOdometerField()}

          {showDateFields && (
            <DateField
              form={form}
              label="Data do agendamento"
              name="date"
              className="flex flex-col"
              placeholder="Selecione uma data"
            />
          )}
        </CardContent>
      </Card>

      {(showOdometerFields || showDateFields) && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Notificações</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {showOdometerFields && (
              <>
                <FormField
                  control={form.control}
                  name="notification_odometer"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <FormLabel className="text-base">
                          Receber notificações na plataforma web/mobile perto do
                          valor de hodômetro
                        </FormLabel>
                      </div>
                      <FormControl>
                        <Switch
                          className="bg-primary-500"
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                {notificationOdometer && (
                  <div className="space-y-4 rounded-lg border p-4">
                    <h3 className="text-lg font-medium">Antecedência</h3>
                    <FormField
                      control={form.control}
                      name="notification_odometer_1000"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center justify-between">
                          <FormLabel>1000 km antes</FormLabel>
                          <FormControl>
                            <Switch
                              className="bg-primary-500"
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="notification_odometer_2500"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center justify-between">
                          <FormLabel>2500 km antes</FormLabel>
                          <FormControl>
                            <Switch
                              className="bg-primary-500"
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="notification_odometer_5000"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center justify-between">
                          <FormLabel>5000 km antes</FormLabel>
                          <FormControl>
                            <Switch
                              className="bg-primary-500"
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>
                )}
              </>
            )}

            {showDateFields && (
              <>
                <FormField
                  control={form.control}
                  name="notification_date"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <FormLabel className="text-base">
                          Receber notificações na plataforma web/mobile perto da
                          data
                        </FormLabel>
                      </div>
                      <FormControl>
                        <Switch
                          className="bg-primary-500"
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                {notificationDate && (
                  <div className="space-y-4 rounded-lg border p-4">
                    <h3 className="text-lg font-medium">Antecedência</h3>
                    <FormField
                      control={form.control}
                      name="notification_date_15"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center justify-between">
                          <FormLabel>15 dias antes</FormLabel>
                          <FormControl>
                            <Switch
                              className="bg-primary-500"
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="notification_date_30"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center justify-between">
                          <FormLabel>30 dias antes</FormLabel>
                          <FormControl>
                            <Switch
                              className="bg-primary-500"
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Recorrência</CardTitle>
        </CardHeader>
        <CardContent className="divide-y">
          <FormField
            control={form.control}
            name="is_recurring"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center justify-between py-4 first:pt-0">
                <div className="space-y-0.5">
                  <FormLabel className="text-base">
                    Repetir automaticamente
                  </FormLabel>
                  <div className="text-sm text-muted-foreground">
                    Ao concluir esta manutenção, a próxima ocorrência é criada
                    automaticamente com o mesmo intervalo
                  </div>
                </div>
                <FormControl>
                  <Switch
                    className="bg-primary-500"
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          {isRecurring && renderRecurringIntervalFields(maintenanceType)}
        </CardContent>
      </Card>
    </>
  );

  const renderEditForm = () => (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Dados básicos</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {renderBasicDataFields()}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Conclusão</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {renderOdometerField()}

          <DateField
            form={form}
            label="Data de ocorrência"
            name="occurrence_date"
            className="flex flex-col"
            placeholder="Selecione uma data"
          />

          {renderValueField()}

          <FormField
            control={form.control}
            name="invoice"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Nota Fiscal</FormLabel>
                <FormControl>
                  <Input {...field} value={field.value ?? ""} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </CardContent>
      </Card>

      {recurringTemplateId && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Recorrência</CardTitle>
          </CardHeader>
          <CardContent className="divide-y">
            <FormField
              control={form.control}
              name="is_recurring"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between py-4 first:pt-0">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      Manutenção recorrente
                    </FormLabel>
                    <div className="text-sm text-muted-foreground">
                      Desative para parar de gerar novas ocorrências ao concluir
                    </div>
                  </div>
                  <FormControl>
                    <Switch
                      className="bg-primary-500"
                      checked={field.value ?? false}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            {isRecurring && renderRecurringIntervalFields(occurrenceType)}
          </CardContent>
        </Card>
      )}
    </>
  );

  if (isLoading) {
    return (
      <div className="mx-auto space-y-6">
        <h1 className="text-2xl font-semibold tracking-tight">
          Editar Manutenção
        </h1>

        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-32" />
          </CardHeader>
          <CardContent className="space-y-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-10 w-full" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-24" />
          </CardHeader>
          <CardContent className="space-y-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-28" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-14 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto space-y-6">
      <h1 className="text-2xl font-semibold tracking-tight">
        {id ? "Editar Manutenção" : "Nova Manutenção"}
      </h1>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {id ? renderEditForm() : renderCreateForm()}

          <div className="flex justify-end space-x-2 pt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/maintenances")}
            >
              Cancelar
            </Button>
            <SubmitButton loading={loading} />
          </div>
        </form>
      </Form>
    </div>
  );
};

export default MaintenanceForm;
